/* =========================================================
   LoadingScreen — 4s branded transition on sign-in and sign-out.
   Ed-tech themed: an e-learning monitor with a playing lesson
   inside an orbiting "connected centers" ring, cycling status
   messages, and a progress bar that fills over the duration.
   ========================================================= */
import { useEffect, useState } from 'react';
import type { Lang } from '@/types';

const COPY: Record<Lang, { in: string[]; out: string[] }> = {
  es: {
    in: ['Conectando con los centros…', 'Sincronizando tus datos…', 'Preparando tu panel…'],
    out: ['Guardando tus cambios…', 'Cerrando tu sesión de forma segura…', '¡Hasta pronto!'],
  },
  en: {
    in: ['Connecting to centers…', 'Syncing your data…', 'Preparing your dashboard…'],
    out: ['Saving your changes…', 'Signing you out securely…', 'See you soon!'],
  },
};

export interface LoadingScreenProps {
  lang?: Lang;
  duration?: number;
  mode?: 'in' | 'out';
}

export function LoadingScreen({ lang = 'es', duration = 4000, mode = 'in' }: LoadingScreenProps) {
  const msgs = (COPY[lang] || COPY.es)[mode];
  const [step, setStep] = useState(0);

  useEffect(() => {
    const per = Math.max(700, Math.floor(duration / msgs.length));
    const id = window.setInterval(() => setStep((s) => (s + 1) % msgs.length), per);
    return () => window.clearInterval(id);
  }, [duration, msgs.length]);

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 50,
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        gap: 30,
        background: 'radial-gradient(900px 600px at 50% 36%, #FFFFFF 0%, var(--bg) 62%)',
      }}
      role="status"
      aria-live="polite"
    >
      {/* emblem: orbiting ring + e-learning screen */}
      <div style={{ position: 'relative', width: 150, height: 150 }}>
        {/* faint track */}
        <div style={{ position: 'absolute', inset: 0, borderRadius: '50%', border: '3px solid var(--line)', opacity: 0.7 }} />
        {/* spinning brand arc */}
        <div
          style={{
            position: 'absolute', inset: 0, borderRadius: '50%',
            background: 'conic-gradient(from 0deg, transparent 0deg, transparent 250deg, var(--brand-primary) 360deg)',
            WebkitMask: 'radial-gradient(farthest-side, transparent calc(100% - 3px), #000 calc(100% - 3px))',
            mask: 'radial-gradient(farthest-side, transparent calc(100% - 3px), #000 calc(100% - 3px))',
            animation: 'etcSpin 1.1s linear infinite',
          }}
        />
        {/* orbiting "connected center" nodes */}
        {[0, 1, 2].map((i) => (
          <div key={i} style={{ position: 'absolute', inset: 0, animation: `etcSpin ${2.6 + i * 0.5}s linear infinite` }}>
            <span
              style={{
                position: 'absolute', top: -4, left: '50%', marginLeft: -4,
                width: 8, height: 8, borderRadius: '50%',
                background: i === 0 ? 'var(--brand-primary)' : 'var(--brand-ink)',
                opacity: i === 0 ? 1 : 0.35,
              }}
            />
          </div>
        ))}
        {/* e-learning monitor: a course playing on screen */}
        <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', animation: 'etcBob 2.4s ease-in-out infinite' }}>
          <svg width="78" height="68" viewBox="0 0 78 68" fill="none">
            <rect x="9" y="8" width="60" height="42" rx="5" fill="var(--brand-ink)" />
            <rect x="13" y="12" width="52" height="34" rx="2.5" fill="var(--surface)" />
            <circle cx="39" cy="29" r="10" fill="var(--brand-primary)" />
            <path d="M36 24 L45 29 L36 34 Z" fill="#fff" />
            <rect x="20" y="41" width="38" height="2.4" rx="1.2" fill="var(--line)" />
            <rect
              x="20" y="41" width="38" height="2.4" rx="1.2" fill="var(--brand-primary)"
              style={{ transformOrigin: '20px 42px', animation: 'etcScreenFill 2.2s ease-in-out infinite' }}
            />
            <rect x="34" y="50" width="10" height="7" fill="var(--brand-ink)" />
            <rect x="24" y="57" width="30" height="4" rx="2" fill="var(--brand-ink)" />
          </svg>
        </div>
      </div>

      {/* wordmark */}
      <div style={{ textAlign: 'center' }}>
        <div className="display" style={{ fontSize: 22, fontWeight: 800, letterSpacing: '-0.01em', color: 'var(--brand-ink)' }}>
          ETC Connect
        </div>
        <div key={step} style={{ marginTop: 8, fontSize: 13.5, fontWeight: 600, color: 'var(--text-2)' }}>
          {msgs[step]}
        </div>
      </div>

      {/* progress bar */}
      <div style={{ width: 220, height: 4, borderRadius: 999, background: 'var(--line)', overflow: 'hidden' }}>
        <div
          style={{
            height: '100%', borderRadius: 999, background: 'var(--brand-primary)',
            transformOrigin: 'left center',
            animation: `etcFill ${duration}ms cubic-bezier(.4,0,.2,1) forwards`,
          }}
        />
      </div>
    </div>
  );
}
